import { authApi, isAuthError } from '@/api/login';
import { ErrorBanner, FormField } from '@/components';
import { PrimaryButton } from '@/components/PrimaryButton';
import { RegisterHeader } from '@/features/register/RegisterHeader';
import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Email-only form that initiates the magic-link / OTP login flow */
export function MagicLoginForm({ onSuccess }: { onSuccess: (email: string) => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValidEmail = EMAIL_REGEX.test(email);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const normalizedEmail = email.trim().toLowerCase();
    try {
      await authApi.startLogin(normalizedEmail);
      onSuccess(normalizedEmail);
    } catch (err) {
      if (isAuthError(err)) {
        setError(err.message);
      } else {
        setError(t('auth.magicLoginError', 'Failed to send login code. Please try again.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full">
      <RegisterHeader
        title={t('auth.magicLogin', 'Sign in with email')}
        subtitle={t('auth.magicLoginSubtitle', 'Enter your email address to receive a sign-in code')}
      />

      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField
          label={t('auth.email', 'Email')}
          type="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
          autoComplete="email"
        />

        <ErrorBanner hasError={!!error}>
          {error}
        </ErrorBanner>

        <PrimaryButton
          type="submit"
          isLoading={isLoading}
          disabled={!isValidEmail}
          fullWidth
          size="lg"
        >
          {t('auth.magicLoginButton', 'Send Code')}
        </PrimaryButton>
      </form>
    </div>
  );
}
